// Sites — 真實資料版（PR #88, Route 2）。
//
// 後端：
//   GET    /v1/sites?tenant_id=&q=&status=&risk=&limit=&offset=
//   GET    /v1/sites/:id
//   POST   /v1/sites
//   PATCH  /v1/sites/:id
//   DELETE /v1/sites/:id    (soft archive → status='inactive')
// 客戶下拉：GET /v1/clients
//
// UI：DataTable + create/edit Drawer + 軟刪除 + skeleton + 友善錯誤 + retry。

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, MapPin, RefreshCw, ServerCrash, Building2, Trash2, Layers, Users as UsersIcon, X, ArrowRight } from "lucide-react";
import PageHeader from "../components/PageHeader.js";
import DataTable, { type Column } from "../components/DataTable.js";
import PaginationControl from "../components/PaginationControl.js";
import { usePagination } from "../lib/usePagination.js";
import Drawer from "../components/Drawer.js";
import EmptyState from "../components/EmptyState.js";
import TenantPickerEmptyState from "../components/TenantPickerEmptyState.js";
import Skeleton from "../components/Skeleton.js";
import { Field } from "../components/Field.js";
import {
  listSites,
  createSite,
  patchSite,
  archiveSite,
  friendlySitesError,
  type SiteRow,
  type SiteStatus,
  type SiteRisk,
} from "../lib/sitesApi.js";
import { listClientsLite, type ClientLite } from "../lib/clientsApi.js";
import { useDashboardSession } from "../auth/useDashboardSession.js";
import { useConfirmDialog } from "../components/ConfirmDialog.js";

const STATUS_BADGE: Record<SiteStatus, string> = {
  active: "badge-success",
  inactive: "badge-default",
};
const STATUS_LABEL: Record<SiteStatus, string> = {
  active: "啟用",
  inactive: "已封存",
};
const RISK_BADGE: Record<SiteRisk, string> = {
  low: "badge-default",
  medium: "badge-warning",
  high: "badge-danger",
};
const RISK_LABEL: Record<SiteRisk, string> = {
  low: "低",
  medium: "中",
  high: "高",
};

type ListState =
  | { status: "loading" }
  | { status: "ready"; rows: SiteRow[]; total: number }
  | { status: "error"; message: string };

type DrawerState =
  | null
  | { mode: "new" }
  | { mode: "edit"; site: SiteRow };

export default function Sites(): React.ReactElement {
  const session = useDashboardSession();
  const tenantParam = session.super_admin ? session.acting_tenant_id ?? null : null;
  // `?client_id=…` is the deep-link contract used by /dashboard/clients's
  // 案場數 column. When present, narrow the list + show a banner naming
  // the active client so the operator never wonders 「what am I looking at」.
  const [searchParams, setSearchParams] = useSearchParams();
  const clientIdFilter = searchParams.get("client_id");
  const [state, setState] = useState<ListState>({ status: "loading" });
  const [editing, setEditing] = useState<DrawerState>(null);
  const [clients, setClients] = useState<ClientLite[]>([]);
  const canWrite = session.scopes.includes("write:site") || session.scopes.includes("admin:tenant") || session.super_admin === true;
  const pag = usePagination({ urlPrefix: "sites_" });
  const activeClient = useMemo(
    () => clients.find((c) => c.id === clientIdFilter) ?? null,
    [clients, clientIdFilter],
  );

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const r = await listSites({ tenantId: tenantParam, limit: pag.limit, offset: pag.offset });
      setState({ status: "ready", rows: r.items, total: r.total });
    } catch (err) {
      setState({ status: "error", message: friendlySitesError(err) });
    }
  }, [tenantParam, pag.limit, pag.offset]);

  const loadClients = useCallback(async () => {
    try {
      const r = await listClientsLite(tenantParam);
      setClients(r.items.map((c) => ({ id: c.id, name: c.name })));
    } catch {
      setClients([]);
    }
  }, [tenantParam]);

  useEffect(() => {
    void load();
    void loadClients();
  }, [load, loadClients]);

  const columns: Column<SiteRow>[] = useMemo(() => [
    {
      key: "name",
      header: "案場名稱",
      primary: true,
      render: (s) => (
        <div className="flex items-start gap-2">
          <MapPin className="h-4 w-4 text-text-muted flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="font-medium text-text-primary truncate">{s.name}</div>
            <div className="text-2xs text-text-muted truncate">{s.address ?? "—"}</div>
          </div>
        </div>
      ),
      width: "35%",
    },
    {
      key: "client",
      header: "客戶",
      hideOnMobile: true,
      render: (s) => <span className="text-text-secondary">{s.client_name ?? "—"}</span>,
    },
    {
      // Clicking the count deep-links into the locations list filtered
      // by this site (parallel to the 案場數 → /sites?client_id behaviour
      // shipped in #359). `0` links too so the operator can fast-path to
      // "show me this site's empty list so I can add the first one".
      key: "locations",
      header: "地點",
      render: (s) => (
        <Link
          to={`/dashboard/locations?site_id=${encodeURIComponent(s.id)}`}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 font-mono tabular-nums text-accent hover:underline"
          title={`查看 ${s.name} 的所有地點`}
        >
          {s.location_count}
          <ArrowRight className="h-3 w-3" />
        </Link>
      ),
    },
    {
      key: "officers",
      header: "在勤",
      render: (s) => <span className="font-mono tabular-nums text-text-secondary">{s.active_officers}</span>,
    },
    {
      key: "risk",
      header: "風險",
      render: (s) => <span className={RISK_BADGE[s.risk_level]}>{RISK_LABEL[s.risk_level]}</span>,
    },
    {
      key: "status",
      header: "狀態",
      render: (s) => <span className={STATUS_BADGE[s.status]}>{STATUS_LABEL[s.status]}</span>,
    },
    {
      key: "layout",
      header: "圖資 / 樓層檢視",
      render: (s) => (
        <div className="flex items-center gap-1.5">
          <Link
            to={`/dashboard/sites/${s.id}/floor-stack`}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 rounded border border-accent/40 px-2 py-0.5 text-xs text-accent hover:bg-accent/10"
            title="進入樓層檢視中控"
          >
            <Layers className="h-3 w-3" />
            樓層檢視
          </Link>
          <Link
            to={`/dashboard/sites/${s.id}/layout`}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 rounded border border-border px-2 py-0.5 text-xs text-text-secondary hover:bg-bg-hover hover:text-text-primary"
            title="編輯樓層圖資"
          >
            編輯
          </Link>
        </div>
      ),
    },
  ], []);

  if (session.super_admin && !tenantParam) {
    return (
      <>
        <PageHeader title="案場 Sites" description="實際巡邏的物理場域，包含定位、區域劃分與地點配置" />
        <TenantPickerEmptyState />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="案場 Sites"
        description="實際巡邏的物理場域，包含定位、區域劃分與地點配置"
        actions={
          <>
            <button data-tour-id="sites.refresh" className="btn-secondary text-sm" onClick={() => void load()} disabled={state.status === "loading"}>
              <RefreshCw className={`h-4 w-4 ${state.status === "loading" ? "animate-spin" : ""}`} /> 重新整理
            </button>
            {canWrite && (
              <button data-tour-id="sites.new" onClick={() => setEditing({ mode: "new" })} className="btn-primary text-sm">
                <Plus className="h-4 w-4" />
                <span>新增案場</span>
              </button>
            )}
          </>
        }
      />
      {clientIdFilter && (
        // Active filter banner — clicking the X clears the query param
        // (drops back to the unfiltered list) without losing other state.
        // Shows the client's display name when we've loaded `clients`,
        // falls back to the raw id while it's still loading.
        <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-accent-subtle border border-accent/40 px-3 py-1.5 text-xs text-accent">
          <UsersIcon className="h-3.5 w-3.5" />
          <span>目前終端客戶：<span className="font-semibold text-text-primary">{activeClient?.name ?? clientIdFilter}</span></span>
          <button
            type="button"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete("client_id");
              setSearchParams(next, { replace: true });
            }}
            className="ml-1 text-accent/70 hover:text-text-primary"
            aria-label="清除篩選"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      {state.status === "loading" && <SitesSkeleton />}
      {state.status === "error" && (
        <EmptyState
          icon={ServerCrash}
          title="案場列表載入失敗"
          description={state.message}
          action={<button className="btn-primary text-sm" onClick={() => void load()}>再試一次</button>}
        />
      )}
      {state.status === "ready" && (() => {
        // Apply the optional client_id filter from the URL. Pull all rows
        // once and narrow client-side so flipping the chip on/off is
        // free; the dataset isn't large enough to justify a server-side
        // round-trip per filter change.
        const visibleRows = clientIdFilter
          ? state.rows.filter((r) => r.client_id === clientIdFilter)
          : state.rows;
        if (visibleRows.length === 0) {
          return (
            <EmptyState
              icon={MapPin}
              title={clientIdFilter ? "這個客戶下還沒有案場" : "這個單位還沒有案場"}
              description={clientIdFilter
                ? "為這個客戶建立第一個案場，再來配置地點與路線。"
                : "建立第一個案場之後，就可以配置地點與排定巡邏路線。"}
              action={canWrite ? <button data-tour-id="sites.new" className="btn-primary text-sm" onClick={() => setEditing({ mode: "new" })}>新增案場</button> : undefined}
            />
          );
        }
        return (
          <>
            <DataTable
              rows={visibleRows}
              columns={columns}
              rowKey={(s) => s.id}
              searchableText={(s) => `${s.name} ${s.address ?? ""} ${s.client_name ?? ""}`}
              onRowClick={(s) => setEditing({ mode: "edit", site: s })}
              serverPagination
            />
            <div className="card mt-3">
              <PaginationControl
                page={pag.page}
                pageSize={pag.limit}
                total={state.total}
                onPageChange={pag.setPage}
                onPageSizeChange={pag.setLimit}
              />
            </div>
          </>
        );
      })()}
      <Drawer
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.mode === "edit" ? `編輯 ${editing.site.name}` : "新增案場"}
        description="案場基本資料、所屬客戶、風險等級"
        size="lg"
      >
        {editing && (
          <SiteForm
            mode={editing.mode}
            site={editing.mode === "edit" ? editing.site : undefined}
            clients={clients}
            tenantId={tenantParam}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              void load();
            }}
            canArchive={canWrite}
          />
        )}
      </Drawer>
    </>
  );
}

function SiteForm({
  mode,
  site,
  clients,
  tenantId,
  onClose,
  onSaved,
  canArchive,
}: {
  mode: "new" | "edit";
  site?: SiteRow;
  clients: ClientLite[];
  tenantId: string | null;
  onClose: () => void;
  onSaved: () => void;
  canArchive: boolean;
}): React.ReactElement {
  const [name, setName] = useState(site?.name ?? "");
  const [clientId, setClientId] = useState(site?.client_id ?? clients[0]?.id ?? "");
  const [address, setAddress] = useState(site?.address ?? "");
  const [risk, setRisk] = useState<SiteRisk>(site?.risk_level ?? "low");
  const [status, setStatus] = useState<SiteStatus>(site?.status ?? "active");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      if (mode === "new") {
        await createSite(
          { client_id: clientId, name: name.trim(), address: address.trim() || null, risk_level: risk, status },
          tenantId,
        );
      } else if (site) {
        await patchSite(
          site.id,
          { client_id: clientId, name: name.trim(), address: address.trim() || null, risk_level: risk, status },
          tenantId,
        );
      }
      onSaved();
    } catch (e) {
      setErr(friendlySitesError(e));
    } finally {
      setBusy(false);
    }
  };

  const dlg = useConfirmDialog();
  const archive = async () => {
    if (!site) return;
    if (!(await dlg.confirm(`確定要封存「${site.name}」？此案場仍會保留歷史資料，但不再可用於新巡邏。`, { destructive: true, okLabel: "封存" }))) return;
    setBusy(true);
    setErr(null);
    try {
      await archiveSite(site.id, tenantId);
      onSaved();
    } catch (e) {
      setErr(friendlySitesError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <Field label="案場名稱" required>
        <input data-tour-id="sites.field.name" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="如：信義 A 大樓" />
      </Field>
      <Field label="所屬客戶" required hint={clients.length === 0 ? "尚未建立任何客戶；請先到 /dashboard/clients 建立" : undefined}>
        <select className="select" value={clientId} onChange={(e) => setClientId(e.target.value)}>
          {clients.length === 0 && <option value="">— 尚無客戶 —</option>}
          {clients.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </Field>
      <Field label="地址">
        <input className="input" value={address} onChange={(e) => setAddress(e.target.value)} />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Field label="風險等級">
          <select className="select" value={risk} onChange={(e) => setRisk(e.target.value as SiteRisk)}>
            <option value="low">低</option>
            <option value="medium">中</option>
            <option value="high">高</option>
          </select>
        </Field>
        <Field label="狀態">
          <select className="select" value={status} onChange={(e) => setStatus(e.target.value as SiteStatus)}>
            <option value="active">啟用</option>
            <option value="inactive">封存</option>
          </select>
        </Field>
        <Field label="地點" hint="自動由配置統計">
          <input className="input font-mono" disabled value={site?.location_count ?? 0} />
        </Field>
      </div>
      {err && <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">{err}</div>}
      <div className="flex flex-row items-center justify-between gap-2">
        <div>
          {mode === "edit" && site && canArchive && (
            <button className="btn-danger text-sm" onClick={() => void archive()} disabled={busy}>
              <Trash2 className="h-4 w-4" /> 封存案場
            </button>
          )}
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="btn-secondary text-sm" disabled={busy}>取消</button>
          <button data-tour-id="sites.save" onClick={() => void save()} className="btn-primary text-sm" disabled={busy || !name.trim() || !clientId}>
            {busy ? "儲存中…" : "儲存"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SitesSkeleton(): React.ReactElement {
  return (
    <div className="card overflow-hidden">
      <div className="p-4 border-b border-border">
        <Skeleton className="h-9 w-72" />
      </div>
      <div className="divide-y divide-border">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center gap-4 p-4">
            <Skeleton className="h-4 w-4 rounded" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-3 w-1/2" />
            </div>
            <Skeleton className="h-5 w-12 hidden sm:block" />
            <Skeleton className="h-5 w-12 hidden sm:block" />
            <Skeleton className="h-5 w-12 hidden md:block" />
          </div>
        ))}
      </div>
    </div>
  );
}
